import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  ReverseImageSearchService,
  ReverseImageSearchParams,
  ReverseImageSearchResult,
} from '../services/reverse-image-search.service';

@Component({
  selector: 'app-reverse-image-search-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-overlay" (click)="closeModal()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h2 class="modal-title">
            <svg viewBox="0 0 24 24" class="title-icon">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                ry="2"
                stroke="currentColor"
                fill="none"
              ></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21,15 16,10 5,21"></polyline>
            </svg>
            Find Similar Items with AI
          </h2>
          <button type="button" class="close-button" (click)="closeModal()">
            <svg viewBox="0 0 24 24" class="close-icon">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div
            *ngIf="!serviceAvailable && !isCheckingService"
            class="service-unavailable"
          >
            <div class="warning-icon">⚠️</div>
            <h3>AI Image Search Unavailable</h3>
            <p>
              The AI-powered image search feature is currently unavailable.
              Please try again later.
            </p>
            <button
              type="button"
              class="retry-btn"
              (click)="checkServiceAvailability()"
            >
              Try Again
            </button>
          </div>

          <div *ngIf="isCheckingService" class="checking-service">
            <div class="loading-spinner"></div>
            <p>Checking AI service availability...</p>
          </div>

          <div *ngIf="serviceAvailable" class="search-interface">
            <div class="upload-section">
              <div
                class="upload-area"
                [class.drag-over]="isDragOver"
                [class.has-image]="selectedFile"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
                (click)="fileInput.click()"
              >
                <input
                  #fileInput
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/avif,image/heic,image/heif"
                  (change)="onFileSelect($event)"
                  style="display: none"
                />

                <div *ngIf="!selectedFile" class="upload-placeholder">
                  <div class="upload-icon">
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      ></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                  </div>
                  <h3>Drop an image here or click to upload</h3>
                  <p>
                    {{ getSupportedFormatsDescription() }} (max 20MB)<br />
                    <small style="color: #6b7280;"
                      >All images converted to PNG for optimal quality</small
                    >
                  </p>
                </div>

                <div
                  *ngIf="selectedFile && canPreviewInBrowser(selectedFile)"
                  class="image-preview"
                >
                  <img [src]="imagePreviewUrl" [alt]="selectedFile.name" />
                  <div class="image-overlay">
                    <button
                      type="button"
                      class="change-image-btn"
                      (click)="$event.stopPropagation(); fileInput.click()"
                    >
                      <svg viewBox="0 0 24 24">
                        <path
                          d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                        ></path>
                        <path
                          d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                        ></path>
                      </svg>
                      Change
                    </button>
                    <button
                      type="button"
                      class="remove-image-btn"
                      (click)="$event.stopPropagation(); removeImage()"
                    >
                      <svg viewBox="0 0 24 24">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path
                          d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                        ></path>
                      </svg>
                      Remove
                    </button>
                  </div>
                </div>

                <div
                  *ngIf="selectedFile && !canPreviewInBrowser(selectedFile)"
                  class="format-placeholder"
                >
                  <div class="format-icon">
                    <svg viewBox="0 0 24 24" class="file-image-icon">
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      ></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21,15 16,10 5,21"></polyline>
                    </svg>
                  </div>
                  <div class="format-info">
                    <h4>
                      {{ getFileFormatName(selectedFile) }} Image Selected
                    </h4>
                    <p class="filename">{{ selectedFile.name }}</p>
                    <p class="filesize">
                      {{ formatFileSize(selectedFile.size) }}
                    </p>
                    <div
                      class="format-badge"
                      [class]="getFormatBadgeClass(selectedFile.type)"
                    >
                      {{ getFileFormatName(selectedFile) }}
                    </div>
                    <div
                      *ngIf="willBeConverted(selectedFile)"
                      class="conversion-notice"
                    >
                      <small style="color: #3b82f6; font-weight: 500;">
                        ✓ Will be converted to PNG automatically
                      </small>
                    </div>
                  </div>
                  <div class="format-actions">
                    <button
                      type="button"
                      class="change-image-btn"
                      (click)="$event.stopPropagation(); fileInput.click()"
                    >
                      <svg viewBox="0 0 24 24">
                        <path
                          d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                        ></path>
                        <path
                          d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                        ></path>
                      </svg>
                      Change
                    </button>
                    <button
                      type="button"
                      class="remove-image-btn"
                      (click)="$event.stopPropagation(); removeImage()"
                    >
                      <svg viewBox="0 0 24 24">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path
                          d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                        ></path>
                      </svg>
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              <div *ngIf="uploadError" class="error-message">
                <svg viewBox="0 0 24 24" class="error-icon">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                {{ uploadError }}
              </div>
            </div>

            <div class="settings-section">
              <div class="setting-group">
                <label class="setting-label">
                  <svg viewBox="0 0 24 24" class="setting-icon">
                    <path
                      d="M4 6h16M4 12h16M4 18h7"
                      stroke="currentColor"
                      fill="none"
                    ></path>
                  </svg>
                  Search Category
                </label>
                <select
                  class="category-select"
                  [(ngModel)]="searchParams.category"
                >
                  <option value="all">All Categories</option>
                  <option value="vehicles">Vehicles</option>
                  <option value="real-estate">Real Estate</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="jobs">Jobs</option>
                </select>
              </div>

              <div class="setting-group">
                <label class="setting-label">
                  <svg viewBox="0 0 24 24" class="setting-icon">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Similarity Threshold:
                  {{
                    ((searchParams.similarity_threshold || 0.7) * 100).toFixed(
                      0
                    )
                  }}%
                </label>
                <div class="slider-container">
                  <input
                    type="range"
                    class="similarity-slider"
                    min="0.5"
                    max="0.95"
                    step="0.05"
                    [(ngModel)]="searchParams.similarity_threshold"
                  />
                  <div class="slider-labels">
                    <span>Less Strict (50%)</span>
                    <span>More Strict (95%)</span>
                  </div>
                </div>
                <p class="similarity-help">
                  Higher values return only very similar items, lower values
                  return broader matches.
                </p>
              </div>

              <div class="setting-group">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    class="checkbox-input"
                    [(ngModel)]="searchParams.include_own_listings"
                  />
                  <span class="checkbox-custom"></span>
                  <span>Include my own listings in results</span>
                </label>
              </div>
            </div>

            <button
              type="button"
              class="search-button"
              [disabled]="!selectedFile || isSearching"
              (click)="performSearch()"
            >
              <div *ngIf="!isSearching" class="button-content">
                <svg viewBox="0 0 24 24" class="search-icon">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <span>Search Similar Items</span>
              </div>
              <div *ngIf="isSearching" class="button-loading">
                <div class="loading-spinner-small"></div>
                <span>Analyzing Image...</span>
              </div>
            </button>

            <div
              *ngIf="hasSearched && quickResults.length > 0"
              #quickResultsSection
              class="quick-results"
            >
              <h3>Quick Results ({{ quickResults.length }} found)</h3>
              <div class="quick-results-grid">
                <div
                  *ngFor="let result of quickResults.slice(0, 6)"
                  class="quick-result-item"
                  (click)="viewFullResults()"
                >
                  <img
                    [src]="result.first_image || '/assets/placeholder.png'"
                    [alt]="result.title"
                  />
                  <div class="result-info">
                    <h4>{{ result.title }}</h4>
                    <span
                      class="similarity-badge"
                      [style.background-color]="
                        getSimilarityColor(result.similarity_score)
                      "
                    >
                      {{ formatSimilarity(result.similarity_score) }} Match
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="view-all-btn"
                (click)="viewFullResults()"
              >
                View All {{ quickResults.length }} Results →
              </button>
            </div>

            <div
              *ngIf="hasSearched && quickResults.length === 0 && !uploadError"
              class="no-results"
            >
              <div class="no-results-icon">🔍</div>
              <h3>No Similar Items Found</h3>
              <p>
                We couldn't find any listings similar to your image. Try
                adjusting the similarity threshold or uploading a different
                image.
              </p>
              <button
                type="button"
                class="try-again-btn"
                (click)="resetSearch()"
              >
                Try Different Image
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./reverse-image-search-modal.component.css'],
})
export class ReverseImageSearchModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('quickResultsSection') quickResultsSection?: ElementRef;

  private destroy$ = new Subject<void>();

  serviceAvailable = true;
  isCheckingService = false;
  selectedFile: File | null = null;
  imagePreviewUrl: string | null = null;
  isDragOver = false;
  uploadError: string | null = null;
  isSearching = false;
  hasSearched = false;
  quickResults: ReverseImageSearchResult[] = [];
  searchImageUrl: string | null = null;
  conversionInfo: any = null;

  searchParams: ReverseImageSearchParams = {
    category: 'all',
    limit: 20,
    similarity_threshold: 0.7,
    include_own_listings: false,
  };

  constructor(
    private reverseImageSearchService: ReverseImageSearchService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.serviceAvailable = true;
    this.isCheckingService = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.imagePreviewUrl) {
      this.reverseImageSearchService.revokeImagePreview(this.imagePreviewUrl);
    }
  }

  checkServiceAvailability(): void {
    this.isCheckingService = true;
    setTimeout(() => {
      this.serviceAvailable = true;
      this.isCheckingService = false;
    }, 500);
  }

  closeModal(): void {
    this.close.emit();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFileSelection(event.dataTransfer.files[0]);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    this.uploadError = null;
    this.conversionInfo = null;

    const validation = this.reverseImageSearchService.validateImageFile(file);
    if (!validation.valid) {
      this.uploadError = validation.error!;
      return;
    }

    if (this.imagePreviewUrl) {
      this.reverseImageSearchService.revokeImagePreview(this.imagePreviewUrl);
    }

    this.selectedFile = file;

    if (this.canPreviewInBrowser(file)) {
      this.imagePreviewUrl =
        this.reverseImageSearchService.createImagePreview(file);
    } else {
      this.imagePreviewUrl = null;
    }

    this.hasSearched = false;
    this.quickResults = [];
  }

  removeImage(): void {
    if (this.imagePreviewUrl) {
      this.reverseImageSearchService.revokeImagePreview(this.imagePreviewUrl);
    }

    this.selectedFile = null;
    this.imagePreviewUrl = null;
    this.uploadError = null;
    this.hasSearched = false;
    this.quickResults = [];
    this.conversionInfo = null;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  performSearch(): void {
    if (!this.selectedFile) return;

    sessionStorage.removeItem('reverseImageSearchResults');

    this.isSearching = true;
    this.uploadError = null;
    this.conversionInfo = null;

    this.reverseImageSearchService
      .uploadAndSearch(this.selectedFile, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSearching = false;
          this.hasSearched = true;
          this.quickResults = response.results;
          this.searchImageUrl = response.search_image_url;
          this.conversionInfo = response.conversion || null;

          if (this.quickResults.length > 0) {
            setTimeout(() => {
              this.scrollToResults();
            }, 100);
          }
        },
        error: (error) => {
          this.isSearching = false;
          this.hasSearched = true;
          this.uploadError = error.message;
          this.quickResults = [];
          this.conversionInfo = null;
        },
      });
  }

  private scrollToResults(): void {
    if (this.quickResultsSection?.nativeElement) {
      this.quickResultsSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  viewFullResults(): void {
    if (!this.quickResults.length) return;

    const searchData = {
      type: 'reverse-image-search',
      searchImageUrl: this.searchImageUrl,
      results: this.quickResults,
      params: this.searchParams,
      conversionInfo: this.conversionInfo,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(
      'reverseImageSearchResults',
      JSON.stringify(searchData)
    );

    this.router.navigate(['/search/reverse-image-results'], {
      queryParams: {
        category: this.searchParams.category,
        threshold: this.searchParams.similarity_threshold,
        timestamp: Date.now(),
      },
    });

    this.closeModal();
  }

  resetSearch(): void {
    this.hasSearched = false;
    this.quickResults = [];
    this.uploadError = null;
    this.searchImageUrl = null;
    this.conversionInfo = null;
  }

  formatSimilarity(score: number): string {
    return this.reverseImageSearchService.formatSimilarityScore(score);
  }

  getSimilarityColor(score: number): string {
    return this.reverseImageSearchService.getSimilarityBadgeColor(score);
  }

  getSupportedFormatsDescription(): string {
    return 'JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, HEIC, SVG';
  }

  getOriginalFormat(): string {
    if (!this.conversionInfo) return '';
    return this.reverseImageSearchService.getFormatDisplayName(
      this.conversionInfo.original_format
    );
  }

  getConversionDetails(): string {
    if (!this.conversionInfo) return '';

    const originalFormat = this.getOriginalFormat();
    const originalSize = this.formatFileSize(this.conversionInfo.original_size);
    const finalSize = this.formatFileSize(this.conversionInfo.converted_size);
    const compressionRatio = this.conversionInfo.compression_ratio;

    return `Converted from ${originalFormat} (${originalSize}) to PNG (${finalSize}) • ${compressionRatio}% size reduction`;
  }

  canPreviewInBrowser(file: File): boolean {
    const nativelySupported = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
    ];

    const limitedSupport = [
      'image/avif',
      'image/x-icon',
      'image/vnd.microsoft.icon',
    ];

    const nonBrowserSupported = [
      'image/heic',
      'image/heif',
      'image/tiff',
      'image/tif',
    ];

    const fileType = file.type.toLowerCase();

    if (nativelySupported.includes(fileType)) {
      return true;
    }

    if (nonBrowserSupported.includes(fileType)) {
      return false;
    }

    if (limitedSupport.includes(fileType)) {
      return true;
    }

    return true;
  }

  getFileFormatName(file: File): string {
    return this.reverseImageSearchService.getFormatDisplayName(file.type);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getFormatBadgeClass(mimeType: string): string {
    if (this.reverseImageSearchService.isModernFormat(mimeType)) {
      return 'format-badge modern';
    }
    if (this.reverseImageSearchService.hasLimitedBrowserSupport(mimeType)) {
      return 'format-badge limited';
    }
    return 'format-badge standard';
  }

  willBeConverted(file: File): boolean {
    return file.type.toLowerCase() !== 'image/png';
  }
}
